<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as UploadingFilesDialogFormInitialData,
  ValidData as UploadingFilesDialogFormValidData,
} from '@/components/dialogForms/UploadingFilesDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import ReportGenerateDialogAlert from '@/components/alerts/ReportGenerateDialogAlert.vue';
import KBtn from '@/components/btns/KBtn.vue';
import UploadingFilesDialogForm from '@/components/dialogForms/UploadingFilesDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (formData: UploadingFilesDialogFormValidData) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: UploadingFilesDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: UploadingFilesDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<UploadingFilesDialogFormInitialData>(() => {
  return {
    to: undefined,
    cc: undefined,
    subject: undefined,
    message: undefined,
    attachments: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.uploadingFilesDialog"
    data-testid="uploadingFilesDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Uploading files
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <UploadingFilesDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
      <ReportGenerateDialogAlert
        icon="mdi-alert-circle-outline"
        :class="$style.alert"
      >
        Only PDF files will be uploaded to drivers run. Other files can be added
        for internal operations use
      </ReportGenerateDialogAlert>
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-download-outline"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            Upload
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.uploadingFilesDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .alert {
    margin-top: 15px;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
